import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivationStart, NavigationCancel, NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NewrelicEvent } from 'qp-newrelic';
import { getBrowserDetector, getDeviceType, getWebOSVisibilityEventName, validateTime } from 'qp-utility';
import { Subject, Subscription } from 'rxjs';
import { filter, pairwise, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { ConvivaService } from './analytics/conviva/conviva.service';
import { DatazoomService } from './analytics/datazoom/service/datazoom.service';
import { NewrelicService } from './analytics/newrelic/service/newrelic.service';
import { DataService } from './data.service';
import { LocalStorageKeys } from './shared/LocalStorageKeys';
import { SessionStorageKeys } from './shared/SessionStorageKeys';
import { applicationConstants } from './shared/applicationConstants';
import { EntitlementService } from './shared/entitlements/entitlement.service';
import { openExitAppDialog } from './shared/modules/errors/exit-page/exit-dialogs';
import { ConfigService } from './shared/services/config/config.service';
import { ExitConfirmationService } from './shared/services/exit-confirmation/exit-confirmation.service';
import { FireboltService } from './shared/services/firebolt.service';
import { LocalStorageService } from './shared/services/local-storage/local-storage.service';
import { RuleService } from './shared/services/location/rule.service';
import { NavigationLibraryService } from './shared/services/navigation/navigation.service';
import { SessionService } from './shared/services/session/session.service';
import { SubscriptionHandlerService } from './shared/services/subscription-handler.service';
import { launchService } from './shared/services/tizen/operation/launchUtil';
import { TizenService } from './shared/services/tizen/tizen.service';
import { UserStateService } from './shared/services/user-state/user-state.service';
import { getSessionInfo } from './shared/utils/EventUtlis';
import { isLoggedIn } from './shared/utils/LoginUtils';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  status = 'ONLINE';
  isAppStartSent = false;
  showExitConfirmation = false;
  isAllowedRegion = true;
  private connectionInterval;
  private destroy$ = new Subject<void>();
  private componentName = 'app';
  constructor(
    private exitService: ExitConfirmationService,
    private translate: TranslateService,
    private navService: NavigationLibraryService,
    private newrelic: NewrelicService,
    private data: DataService,
    private router: Router,
    private tizenService: TizenService,
    private readonly appConfig: ConfigService,
    private readonly userState: UserStateService,
    private readonly datazoomService: DatazoomService,
    private readonly datazoom: DatazoomService,
    private readonly localStorage: LocalStorageService,
    private readonly fireboltService: FireboltService,
    private ruleService: RuleService,
    private readonly matDialog: MatDialog,
    private readonly subscriptionHandlerService: SubscriptionHandlerService,
    private readonly convivaService: ConvivaService,
    private readonly sessionService: SessionService,
    private readonly entitlementService: EntitlementService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.entitlementService.setNewrelicUserInfo();
    this.datazoomService.init();
    if (!!!this.localStorage.getItem(LocalStorageKeys.AUTOPLAY_PREFERENCE)) {
      this.localStorage.setItem(LocalStorageKeys.AUTOPLAY_PREFERENCE, 'true');
    }
    if (isLoggedIn(this.localStorage)) {
      if (!this.tizenService.havePayload()) this.userState.startLoading();
    }

    if (!this.localStorage.getItem(LocalStorageKeys.LANG)) {
      this.localStorage.setItem(LocalStorageKeys.LANG, this.appConfig.applicationConfig.defaultLanguage);
    }
    this.localStorage.setItem(LocalStorageKeys.NAVIGATED, 'false');
    const routerEvents: Subscription = this.router.events
      .pipe(
        filter((evt: any) => evt instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe(() => {
        this.localStorage.setItem(LocalStorageKeys.NAVIGATED, 'true');
      });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'routerEvents', routerEvents);

    if (getBrowserDetector().isTizen()) {
      try {
        launchService(applicationConstants.TIZEN_BG_SERVICE_ID);
        window.addEventListener('appcontrol', () => this.tizenService.checkOperation());
      } catch (e) {
        console.log('appcontrol error ' + e.message);
      }
    } else if (getBrowserDetector().isWebos()) {
      const { hidden, visibilityChange, valid } = getWebOSVisibilityEventName();
      if (valid) {
        document.addEventListener(
          visibilityChange,
          () => {
            this.data.updateAppState(!document[hidden]);
            if (document[hidden]) {
              this.data.updateAppState(false);
              this.newrelic.sendEvent(NewrelicEvent.APP_BACKGROUND);
              this.datazoom.reportBackground();
            } else {
              // this.endedVideo();
              this.userState.hideSplashScreen();
              this.newrelic.sendEvent(NewrelicEvent.APP_FOREGROUND);
              this.datazoom.reportForeground();
            }
          },
          true
        );
      }
    }
    this.convivaService.init();
  }

  async ngOnInit(): Promise<void> {
    this.fireboltService.init();
    const routerEvents: Subscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof ActivationStart))
      .subscribe((event) => {
        if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
          this.data.previousUrl = this.data.currentUrl;
          this.data.currentUrl = event.url;
        } else if (event instanceof ActivationStart) {
          if (
            event?.snapshot?.data !== undefined &&
            !event.snapshot.data.isStorefront &&
            !event.snapshot.data.isStorefrontTv &&
            !event.snapshot.data.isTvMenu &&
            !event.snapshot.data.isApp &&
            !this.isAppStartSent &&
            !sessionStorage.getItem(SessionStorageKeys.ELAPSED_STOREFRONT)
          ) {
            if (!this.tizenService.havePayload()) {
              this.userState.hideSplashScreen();
              this.data.isAppLoaded = true;
            }
            this.isAppStartSent = true;
            const timeInfo = this.newrelic.getTimeSinceLoadInfo();
            if (validateTime(timeInfo.timeSinceLoad, applicationConstants.MAX_START_TIME)) {
              this.newrelic.sendEvent(NewrelicEvent.APP_START);
            }
          } else {
            // should stop from storefront
            if (isLoggedIn(this.localStorage)) {
              if (!this.tizenService.havePayload()) {
                this.userState.hideSplashScreen(false);
                this.data.isAppLoaded = true;
              }
            }
          }
        }
      });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'routerEventsOnInit', routerEvents);

    this.trackExitConfirmation();
    this.setFont();
    this.setUserPreference();
    this.checkConnection();
    this.navService.init();
    this.document.body.classList.remove('-en', '-tg', '-tm');
    this.document.body.classList.add(`-${this.localStorage.getItem(LocalStorageKeys.LANG)}`);
    this.isAllowedRegion = this.ruleService.isAllowedRegion();
    const startupTime = parseInt(sessionStorage.getItem(SessionStorageKeys.ELAPSED_SPLASH), 10);
    const result = new Date().getTime() - startupTime;
    sessionStorage.setItem(SessionStorageKeys.APP_STARTUP_TIME, '' + result);
    this.newrelic.store.setTimeToAppStartUp(result);
    this.newrelic.store.setSessionInfo(getSessionInfo(sessionStorage, this.sessionService));
    if (!this.isAllowedRegion) {
      this.userState.hideSplashScreen(true);
    }
    this.fireboltService.ready();
  }

  ngOnDestroy(): void {
    this.subscriptionHandlerService.unsubscribeAllFromComponent(this.componentName);
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.connectionInterval);
    this.localStorage.removeItem(LocalStorageKeys.LAUNCH_COUNT);
    this.convivaService.destroy();
    this.fireboltService.close();
  }

  appExit(): void {
    this.exitService.closeApp();
  }

  /**
   * subscribe to exit service observable
   */
  private trackExitConfirmation(): void {
    const exit: Subscription = this.exitService
      .listenExitConfirmation()
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => {
        this.showExitConfirmation = e;
        this.showExitConfirmation && openExitAppDialog(this.matDialog);
      });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'exit', exit);
  }

  private setFont() {
    document.getElementsByTagName('body')[0].classList.add('dt_' + getDeviceType());
  }

  private setUserPreference() {
    sessionStorage.setItem(SessionStorageKeys.SESSIONID, uuidv4());
    const lang = this.localStorage.getItem(LocalStorageKeys.LANG);

    this.newrelic.store.setLanguageInfo({
      contentLanguage: lang,
      displayLanguage: lang,
      updatedFromHomePage: false,
    });

    if (lang) {
      this.translate.use(lang);
      this.translate.setDefaultLang(lang);
    } else {
      this.translate.use('es');
      this.translate.setDefaultLang('es');
    }
  }

  private checkConnection() {
    window.addEventListener(
      'online',
      () => {
        this.status = 'ONLINE';
      },
      { passive: true }
    );
    window.addEventListener(
      'offline',
      () => {
        this.status = 'OFFLINE';
        this.navService.focusContainer('error-dialog-box-buttons', true, 0, false);
      },
      { passive: true }
    );
  }
}
